import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  CheckCircle2, 
  AlertCircle, 
  X, 
  Sparkles,
  ShieldAlert
} from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // حذف التنبيه (المنطق الأصلي)
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // إضافة تنبيه جديد (المنطق الأصلي كاملاً)
  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration,
      createdAt: new Date()
    };

    setToasts(prev => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  // الدوال المساعدة (Wrappers)
  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);
  const info = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// حاوية التنبيهات (التي تظهر في زاوية الشاشة)
const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-6 left-6 z-[9999] flex flex-col gap-4 w-full max-w-[400px] pointer-events-none">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

// عنصر التنبيه الفردي (التصميم المطور)
const ToastItem = ({ toast, onClose }) => {
  const [progress, setProgress] = useState(100);

  // تحديث شريط التقدم بصرياً
  useEffect(() => {
    const interval = 10;
    const step = (interval / toast.duration) * 100;
    
    const timer = setInterval(() => {
      setProgress(prev => Math.max(0, prev - step));
    }, interval);

    return () => clearInterval(timer);
  }, [toast.duration]);

  const config = {
    success: {
      icon: <CheckCircle2 size={22} />,
      bgColor: 'bg-white/90',
      accentColor: 'bg-emerald-500',
      textColor: 'text-emerald-900',
      iconColor: 'text-emerald-500',
      shadow: 'shadow-emerald-100',
      label: 'نجاح العملية'
    },
    error: {
      icon: <ShieldAlert size={22} />,
      bgColor: 'bg-white/90',
      accentColor: 'bg-rose-500',
      textColor: 'text-rose-900',
      iconColor: 'text-rose-500',
      shadow: 'shadow-rose-100',
      label: 'خطأ في النظام'
    },
    warning: {
      icon: <AlertCircle size={22} />,
      bgColor: 'bg-white/90',
      accentColor: 'bg-amber-500',
      textColor: 'text-amber-900',
      iconColor: 'text-amber-500',
      shadow: 'shadow-amber-100',
      label: 'تنبيه هام'
    },
    info: {
      icon: <Sparkles size={22} />,
      bgColor: 'bg-white/90',
      accentColor: 'bg-primary-500',
      textColor: 'text-primary-900',
      iconColor: 'text-primary-500',
      shadow: 'shadow-primary-100',
      label: 'إشعار جديد'
    }
  };

  const style = config[toast.type] || config.info;

  return (
    <div className={`
      pointer-events-auto relative group overflow-hidden
      ${style.bgColor} backdrop-blur-xl border border-white
      rounded-[1.5rem] shadow-2xl ${style.shadow}
      p-5 flex items-center gap-4 transition-all duration-500
      animate-in slide-in-from-left-full
    `}>
      
      {/* Icon Area */}
      <div className={`
        flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center
        ${style.iconColor} bg-opacity-10 bg-current transition-transform group-hover:scale-110
      `}>
        {style.icon}
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0 pr-2 text-right">
        <p className={`text-[10px] font-black uppercase tracking-widest ${style.iconColor} mb-1 opacity-80`}>
          {style.label}
        </p>
        <p className={`text-sm font-bold leading-relaxed ${style.textColor} break-words`}>
          {toast.message}
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-all active:scale-90"
      >
        <X size={18} />
      </button>

      {/* Visual Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100/50">
        <div 
          className={`h-full ${style.accentColor} transition-all duration-100 ease-linear shadow-[0_0_8px_rgba(0,0,0,0.1)]`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default ToastProvider;